import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import { Glyph } from "./staff/glyphs";
import InstructionAudioButton from "./InstructionAudioButton";

function SymbolGlyph({ symbol, size = 58 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-20 -48 72 96"
      aria-hidden="true"
      style={{ display: "block", overflow: "visible" }}
    >
      <g transform="translate(5, 6) scale(1.25)">
        <Glyph symbol={symbol} stemUp />
      </g>
    </svg>
  );
}

export default function SymbolMatchingWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [draggingSymbol, setDraggingSymbol] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const isReview = mode === "review";
  const items = config.items || [];
  const symbols = config.symbols || [];
  const usedSymbols = new Set(Object.values(value).filter(Boolean));

  function placeSymbol(itemId, symbol) {
    if (mode !== "edit" || !symbol) return;

    const next = { ...value };

    // Each bank symbol is a single movable tile. If it was already placed,
    // move it rather than duplicating it.
    Object.keys(next).forEach((key) => {
      if (next[key] === symbol) next[key] = "";
    });

    next[itemId] = symbol;
    onChange(next);
    setSelectedSymbol(null);
    setDraggingSymbol(null);
    setHoveredItem(null);
  }

  function clearItem(itemId) {
    if (mode !== "edit") return;
    onChange({ ...value, [itemId]: "" });
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div style={{ display: "grid", gap: 22, color: "#000" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            alignItems: "center"
          }}
        >
          {symbols.map((entry) => {
            const symbol = typeof entry === "string" ? entry : entry.symbol;
            const label = typeof entry === "string" ? entry : entry.label;
            const alreadyUsed = usedSymbols.has(symbol);
            const isSelected = selectedSymbol === symbol;

            return (
              <button
                key={symbol}
                type="button"
                disabled={isReview || alreadyUsed}
                draggable={!isReview && !alreadyUsed}
                onDragStart={(e) => {
                  setDraggingSymbol(symbol);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", symbol);
                }}
                onDragEnd={() => {
                  setDraggingSymbol(null);
                  setHoveredItem(null);
                }}
                onClick={() => {
                  if (isReview || alreadyUsed) return;
                  setSelectedSymbol(isSelected ? null : symbol);
                }}
                aria-label={label || symbol}
                title={label || undefined}
                style={{
                  minWidth: 84,
                  minHeight: 76,
                  display: "grid",
                  placeItems: "center",
                  padding: "4px 12px",
                  borderRadius: 8,
                  border: isSelected ? "2px solid #1976d2" : "1px solid #aaa",
                  background: alreadyUsed ? "#f2f2f2" : isSelected ? "#e3f2fd" : "#fff",
                  opacity: alreadyUsed ? 0.4 : 1,
                  cursor: isReview || alreadyUsed ? "default" : "grab"
                }}
              >
                <SymbolGlyph symbol={symbol} />
              </button>
            );
          })}
        </div>

        <div style={{ display: "grid", gap: 12, maxWidth: 520 }}>
          {items.map((item) => {
            const current = value[item.id] || "";
            const part = feedback?.parts?.[item.id];
            const border = isReview
              ? part?.correct
                ? "2px solid #2e7d32"
                : "2px solid #c62828"
              : hoveredItem === item.id
              ? "2px solid #1976d2"
              : selectedSymbol
              ? "2px dashed #1976d2"
              : "2px solid #aaa";

            return (
              <div
                key={item.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(130px, 1fr) 120px",
                  alignItems: "center",
                  gap: 16
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 17,
                    fontWeight: 600
                  }}
                >
                  <span>{item.label}</span>
                  {item.audio && (
                    <InstructionAudioButton
                      src={item.audio}
                      label={`Play ${item.label}`}
                    />
                  )}
                </div>

                <button
                  type="button"
                  disabled={isReview}
                  onDragOver={(e) => {
                    if (mode !== "edit" || !draggingSymbol) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setHoveredItem(item.id);
                  }}
                  onDragLeave={() => setHoveredItem(null)}
                  onDrop={(e) => {
                    if (mode !== "edit") return;
                    e.preventDefault();
                    const symbol = draggingSymbol || e.dataTransfer.getData("text/plain");
                    placeSymbol(item.id, symbol);
                  }}
                  onClick={() => {
                    if (isReview) return;
                    if (selectedSymbol) {
                      placeSymbol(item.id, selectedSymbol);
                    } else if (current) {
                      clearItem(item.id);
                    }
                  }}
                  aria-label={`${item.label} answer blank`}
                  style={{
                    height: 82,
                    display: "grid",
                    placeItems: "center",
                    padding: 4,
                    borderRadius: 8,
                    border,
                    background: isReview
                      ? part?.correct
                        ? "#e8f5e9"
                        : "#fdecea"
                      : current
                      ? "#fafafa"
                      : "#fff",
                    cursor: isReview ? "default" : selectedSymbol ? "pointer" : current ? "pointer" : "default"
                  }}
                >
                  {current ? (
                    <SymbolGlyph symbol={current} />
                  ) : (
                    <span style={{ color: "#777", fontSize: 14 }}>Drop here</span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </QuestionLayout>
  );
}
